import React, { useState, useEffect, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, Treemap, AreaChart, Area, LineChart, Line } from 'recharts';
import { Package, AlertCircle, CheckCircle, TrendingUp, TrendingDown, Layers, BarChart as BarChartIcon, PieChart as PieChartIcon, ArrowRight, Info, Clock, Calendar, RefreshCw, Settings, Activity, Zap, ListIcon, PackageIcon, ActivityIcon, X } from 'lucide-react';
import { motion } from 'framer-motion';

// Örnek veri - Gerçek veriler props olarak veya API'dan alınmalıdır
const sampleDeviceModels = [
  {
    id: 'dm001',
    name: 'Endüstriyel Robot Kolu XR-5000',
    category: 'Robotik Otomasyon',
    status: 'Aktif',
    components: [
      { productId: 'prod001', productName: 'Servo Motor 750W', quantity: 4, unit: 'adet' },
      { productId: 'prod002', productName: 'PLC Kontrol Ünitesi', quantity: 1, unit: 'adet' },
    ],
  },
  {
    id: 'dm002',
    name: 'Plastix 400',
    category: '3D PRINTER',
    status: 'Geliştirme Aşamasında',
    components: [
      { productId: 'prod001', productName: 'Servo Motor 750W', quantity: 2, unit: 'adet' }, // XR-5000 ile ortak bileşen
      { productId: 'prod003', productName: 'Alüminyum Profil 1m', quantity: 12, unit: 'metre' },
    ],
  },
  {
    id: 'dm003',
    name: 'CNC Freze Makinesi Pro-700',
    category: 'CNC Makineleri',
    status: 'Aktif',
    components: [
      { productId: 'prod002', productName: 'PLC Kontrol Ünitesi', quantity: 1, unit: 'adet' }, // XR-5000 ile ortak bileşen
      { productId: 'prod004', productName: 'Sensör Kiti XY-2', quantity: 2, unit: 'set' },
      { productId: 'prod005', productName: 'Spindle Motor 5kW', quantity: 1, unit: 'adet' },
    ],
  },
];

const sampleAllProducts = [
  { id: 'prod001', name: 'Servo Motor 750W', stock: 10 },
  { id: 'prod002', name: 'PLC Kontrol Ünitesi', stock: 5 },
  { id: 'prod003', name: 'Alüminyum Profil 1m', stock: 50 },
  { id: 'prod004', name: 'Sensör Kiti XY-2', stock: 8 },
  { id: 'prod005', name: 'Spindle Motor 5kW', stock: 3 },
  { id: 'prod006', name: 'Lineer Kızak Seti', stock: 12 },
];

// Modern, kurumsal renk paleti (daha sade tonlar)
const COLORS = ['#0ea5e9', '#6366f1', '#8b5cf6', '#0891b2', '#0d9488', '#4f46e5'];
const STATUS_COLORS = {
  healthy: '#0ea5e9',  // Soft blue
  warning: '#f59e0b',  // Soft amber
  critical: '#ef4444', // Soft red
};

// Gölge ve glassmorphism için CSS sınıfları
const glassMorphism = {
  light: "bg-white/80 backdrop-blur-md border border-white/20 shadow-sm",
  dark: "bg-gray-800/80 backdrop-blur-md border border-gray-700/20 shadow-sm",
  blue: "bg-blue-500/10 backdrop-blur-md border border-blue-500/20 shadow-sm",
  purple: "bg-purple-500/10 backdrop-blur-md border border-purple-500/20 shadow-sm",
};

const calculateProducibleQuantity = (deviceModel, allProducts) => {
  if (!deviceModel.components || deviceModel.components.length === 0) {
    return Infinity; // Reçetesi yoksa sonsuz üretilebilir (teorik olarak)
  }

  let minProducible = Infinity;

  for (const component of deviceModel.components) {
    const productInStock = allProducts.find(p => p.id === component.productId);
    if (!productInStock || productInStock.stock === undefined) {
      return 0; // Gerekli bileşen stokta yoksa hiç üretilemez
    }
    if (component.quantity <= 0) continue; // Gerekli miktar 0 veya negatifse bu bileşeni atla

    const producibleWithThisComponent = Math.floor(productInStock.stock / component.quantity);
    minProducible = Math.min(minProducible, producibleWithThisComponent);
  }
  return minProducible === Infinity ? 0 : minProducible; // Eğer hiç bileşen işlenmediyse (hepsi 0 miktarlıysa) 0 döndür
};

const MakineMontajIstatistikleri = ({ deviceModels = sampleDeviceModels, allProducts = sampleAllProducts }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('overview'); // 'overview', 'devices', 'categories', 'stocks'
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [activeTimeframe, setActiveTimeframe] = useState('monthly');
  
  // Ayarlar için yeni state'ler
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settings, setSettings] = useState({
    chartColors: 'default', // 'default', 'pastel', 'vivid', 'monochrome'
    showAnimations: true,
    dataRefreshInterval: 0, // 0: manuel, 30: her 30 saniye, 60: her dakika
    criticalStockThreshold: 5,
    lowStockThreshold: 10,
    darkMode: false, // Tema kontrolü için
    displayOptions: {
      showTrends: true,
      showLegends: true,
      compactView: false
    }
  });
  
  // Stok tablosu için filtreleme ve sıralama state'leri
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortOption, setSortOption] = useState('name');

  const producibleQuantities = useMemo(() => {
    if (!deviceModels || !allProducts) return [];
    return deviceModels.map(model => ({
      name: model.name,
      producible: calculateProducibleQuantity(model, allProducts),
      category: model.category,
    }));
  }, [deviceModels, allProducts]);

  const categoryProductionData = useMemo(() => {
    const categoryMap = {};
    producibleQuantities.forEach(item => {
      if (!categoryMap[item.category]) {
        categoryMap[item.category] = { name: item.category, totalProducible: 0, deviceCount: 0 };
      }
      categoryMap[item.category].totalProducible += item.producible;
      categoryMap[item.category].deviceCount += 1;
    });
    return Object.values(categoryMap).filter(cat => cat.totalProducible > 0);
  }, [producibleQuantities]);

  const totalProducibleUnits = useMemo(() => {
    return producibleQuantities.reduce((sum, item) => sum + item.producible, 0);
  }, [producibleQuantities]);

  const mostProducibleDevice = useMemo(() => {
    if (producibleQuantities.length === 0) return null;
    return producibleQuantities.reduce((max, item) => item.producible > max.producible ? item : max, producibleQuantities[0]);
  }, [producibleQuantities]);

  const leastProducibleDevice = useMemo(() => {
    if (producibleQuantities.length === 0) return null;
    // Sadece 0'dan büyük olanlar arasında en az üretilebileni bul
    const producibleItems = producibleQuantities.filter(item => item.producible > 0);
    if (producibleItems.length === 0) {
        // Hepsi 0 ise, ilkini (0 olanı) döndür
        return producibleQuantities.length > 0 ? producibleQuantities[0] : null;
    }
    return producibleItems.reduce((min, item) => item.producible < min.producible ? item : min, producibleItems[0]);
  }, [producibleQuantities]);

  const stockStatusData = useMemo(() => {
    if (!allProducts) return [];
    const criticalStockThreshold = 5; // Örneğin, 5 adetten az olanlar kritik
    const lowStockThreshold = 10;    // Örneğin, 10 adetten az olanlar düşük

    let healthy = 0;
    let low = 0;
    let critical = 0;

    allProducts.forEach(product => {
      if (product.stock <= criticalStockThreshold) critical++;
      else if (product.stock <= lowStockThreshold) low++;
      else healthy++;
    });

    return [
      { name: 'Yeterli Stok', value: healthy, color: STATUS_COLORS.healthy },
      { name: 'Düşük Stok', value: low, color: STATUS_COLORS.warning },
      { name: 'Kritik Stok', value: critical, color: STATUS_COLORS.critical },
    ].filter(item => item.value > 0);
  }, [allProducts]);

  // Kritik stok ürünlerinin listesi
  const criticalStockProducts = useMemo(() => {
    if (!allProducts) return [];
    const criticalStockThreshold = 5;
    return allProducts
      .filter(product => product.stock <= criticalStockThreshold)
      .sort((a, b) => a.stock - b.stock)
      .slice(0, 5); // En kritik 5 ürün
  }, [allProducts]);

  // Üretim trendi verisi (örnek veri)
  const productionTrendData = useMemo(() => {
    return [
      { name: 'Ocak', uretim: 12, stok: 18 },
      { name: 'Şubat', uretim: 19, stok: 22 },
      { name: 'Mart', uretim: 15, stok: 20 },
      { name: 'Nisan', uretim: 25, stok: 28 },
      { name: 'Mayıs', uretim: 32, stok: 30 },
      { name: 'Haziran', uretim: 29, stok: 25 },
    ];
  }, []);

  // Geniş değişiklik yerine, stok verisi ile üretilebilir adet miktarı verisi
  const stockAndProducibleData = useMemo(() => {
    // Toplam stok sayısını hesapla
    const totalStock = allProducts.reduce((sum, product) => sum + product.stock, 0);
    
    // Kategorilere göre gruplandırılmış üretilebilir adet verisi
    const categoryData = {};
    producibleQuantities.forEach(item => {
      if (!categoryData[item.category]) {
        categoryData[item.category] = 0;
      }
      categoryData[item.category] += item.producible;
    });
    
    // Verileri grafik için uygun formata dönüştür
    return Object.keys(categoryData).map(category => ({
      name: category,
      totalProducible: categoryData[category],
      totalStock: totalStock
    }));
  }, [allProducts, producibleQuantities]);

  // Zaman aralığına dayalı stok ve üretilebilir miktar verisi
  const timeframeData = useMemo(() => {
    // Zaman aralığı değiştiyse, geçici olarak eski verileri göster
    // Gerçek uygulamada bu API'dan çekilecek gerçek veriler olacaktır
    
    // Kategori bazlı üretilebilir miktarlar - kategorileri kullanarak tek gösterim
    const categoryBasedData = categoryProductionData.map(category => ({
      name: category.name,
      totalProducible: category.totalProducible,
      totalStock: allProducts.reduce((sum, product) => sum + product.stock, 0) / categoryProductionData.length
    }));
    
    // Toplam stok ve üretilebilir miktarlar - zaman tabanlı görünüm yok, sadece mevcut durum
    const totalStock = allProducts.reduce((sum, product) => sum + product.stock, 0);
    const totalProducible = producibleQuantities.reduce((sum, item) => sum + item.producible, 0);
    
    switch (activeTimeframe) {
      case 'weekly':
        // Günlük bazda gösterim yerine kategori bazlı gösterim - gerçek veriyi yansıtır
        return categoryBasedData;
        
      case 'yearly':
        // Aylık bazda gösterim yerine cihaz bazlı gösterim - gerçek veriyi yansıtır
        return producibleQuantities.map(item => ({
          name: item.name,
          totalProducible: item.producible,
          totalStock: Math.round(totalStock / producibleQuantities.length) // Ortalama dağıtım
        }));
        
      case 'monthly':
      default:
        // Haftalık bazda gösterim yerine kategori bazlı - varsayılan görünüm
        return categoryBasedData;
    }
  }, [activeTimeframe, categoryProductionData, producibleQuantities, allProducts]);

  // Üretim verimliliği verisi - gerçek kategorileri kullanarak
  const efficiencyData = useMemo(() => {
    // Gerçek kategoriler üzerinden verimlilik hesaplaması
    if (!categoryProductionData.length) return [];
    
    // Her kategori için yapay bir verimlilik hesabı yapalım
    // Gerçek uygulamada bu veri muhtemelen API'dan gelir veya gerçek bir hesaplama kullanır
    return categoryProductionData.map(category => {
      // Üretilebilir miktar ve ürün sayısına bağlı verimlilik formülü
      // Burada miktar/cihaz sayısına göre yüzde hesabı yapıyoruz (gösterim amaçlıdır)
      const deviceCount = category.deviceCount || 1;
      const avgProducible = category.totalProducible / deviceCount;
      
      // Basit bir hesap: 100 adetten fazla üretilebiliyorsa %90+, 
      // 50-100 arası %80+, 20-50 arası %70+, 20'den az ise %60+ verimlilik
      let efficiency = 60; // Baz verimlilik
      
      if (avgProducible > 100) efficiency = 90 + Math.floor(Math.random() * 10);
      else if (avgProducible > 50) efficiency = 80 + Math.floor(Math.random() * 10);
      else if (avgProducible > 20) efficiency = 70 + Math.floor(Math.random() * 10);
      else efficiency = 60 + Math.floor(Math.random() * 10);
      
      // Verimlilik değeri 100'ü geçmemeli
      efficiency = Math.min(efficiency, 100);
      
      return {
        name: category.name,
        verimlilik: efficiency
      };
    });
  }, [categoryProductionData]);

  // Filtrelenmiş ve sıralanmış ürünler
  const filteredProducts = useMemo(() => {
    if (!allProducts) return [];
    
    // Önce arama metnine göre filtreleme
    let filtered = allProducts.filter(product => {
      if (searchText === '') return true;
      
      const search = searchText.toLowerCase().trim();
      const productName = product.name.toLowerCase();
      const productId = product.id.toLowerCase();
      
      return productName.includes(search) || productId.includes(search);
    });
    
    // Sonra stok durumuna göre filtreleme
    if (statusFilter !== 'all') {
      filtered = filtered.filter(product => {
        const status = 
          product.stock <= 2 ? 'kritik' : 
          product.stock <= 5 ? 'düşük' : 'yeterli';
        return status === statusFilter;
      });
    }
    
    // Son olarak sıralama
    return filtered.sort((a, b) => {
      switch (sortOption) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'name_desc':
          return b.name.localeCompare(a.name);
        case 'stock_asc':
          return a.stock - b.stock;
        case 'stock_desc':
          return b.stock - a.stock;
        default:
          return 0;
      }
    });
  }, [allProducts, searchText, statusFilter, sortOption]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    // Simulating data fetching delay
    const timer = setTimeout(() => {
      if (!deviceModels || !allProducts) {
        setError('İstatistikleri hesaplamak için gerekli veri bulunamadı.');
      }
      setLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, [deviceModels, allProducts]);

  // Zaman aralığı değiştiğinde geri bildirim göster
  const [timeframeMessage, setTimeframeMessage] = useState('');
  
  useEffect(() => {
    // Zaman aralığı değiştiğinde bildirim göster
    const timeframeLabels = {
      weekly: 'Haftalık',
      monthly: 'Aylık',
      yearly: 'Yıllık'
    };
    
    setTimeframeMessage(`${timeframeLabels[activeTimeframe]} görünüme geçildi`);
    
    // 3 saniye sonra mesajı temizle
    const timer = setTimeout(() => {
      setTimeframeMessage('');
    }, 3000);
    
    return () => clearTimeout(timer);
  }, [activeTimeframe]);

  const handleRefresh = () => {
    setLoading(true);
    setTimeout(() => {
      setLastUpdated(new Date());
      setLoading(false);
    }, 800);
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };
  
  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };
  
  const handleSortChange = (e) => {
    setSortOption(e.target.value);
  };

  // Filtreleme için ek handler fonksiyonları
  const toggleSortByName = () => {
    if (sortOption === 'name') {
      // Aynı sıralama seçeneği tekrar tıklandığında tersine çevir
      setSortOption('name_desc');
    } else {
      setSortOption('name');
    }
  };
  
  const toggleSortByStock = () => {
    if (sortOption === 'stock_asc') {
      setSortOption('stock_desc');
    } else {
      setSortOption('stock_asc');
    }
  };

  // Ayar değişikliklerini işlemek için fonksiyon
  const handleSettingChange = (setting, value) => {
    setSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  const handleDisplayOptionChange = (option, value) => {
    setSettings(prev => ({
      ...prev,
      displayOptions: {
        ...prev.displayOptions,
        [option]: value
      }
    }));
  };

  // Renk paletini ayarlara göre belirleme
  const chartColors = useMemo(() => {
    switch(settings.chartColors) {
      case 'pastel':
        return ['#a8d8ea', '#aa96da', '#fcbad3', '#ffffd2', '#a6e4d0'];
      case 'vivid':
        return ['#ff6b6b', '#4ecdc4', '#ffbe0b', '#5465ff', '#3d5a80'];
      case 'monochrome':
        return ['#0466c8', '#0353a4', '#023e7d', '#002855', '#001845'];
      default:
        return COLORS; // Default renkler
    }
  }, [settings.chartColors]);

  // JSX için ayarlar modalı bileşeni
  const SettingsModal = () => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={() => setShowSettingsModal(false)}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        transition={{ type: "spring", stiffness: 300, damping: 25 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
            <Settings size={20} className="mr-2 text-blue-500" />
            İstatistik Ayarları
          </h2>
          <button
            onClick={() => setShowSettingsModal(false)}
            className="p-2 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X size={20} />
          </button>
        </div>
        
        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Renk Paleti Seçimi */}
          <div className="space-y-2">
            <h3 className="text-md font-medium text-gray-800 dark:text-white">Grafik Renk Paleti</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {['default', 'pastel', 'vivid', 'monochrome'].map((palette) => (
                <button
                  key={palette}
                  onClick={() => handleSettingChange('chartColors', palette)}
                  className={`p-2 rounded-lg flex flex-col items-center ${
                    settings.chartColors === palette 
                      ? 'ring-2 ring-blue-500 shadow-md' 
                      : 'border border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div className="flex mb-2">
                    {palette === 'default' && (
                      COLORS.slice(0, 3).map((color, i) => (
                        <div key={i} className="w-4 h-4 rounded-full" style={{ backgroundColor: color, marginLeft: i > 0 ? '-5px' : 0 }}></div>
                      ))
                    )}
                    {palette === 'pastel' && (
                      ['#a8d8ea', '#aa96da', '#fcbad3'].map((color, i) => (
                        <div key={i} className="w-4 h-4 rounded-full" style={{ backgroundColor: color, marginLeft: i > 0 ? '-5px' : 0 }}></div>
                      ))
                    )}
                    {palette === 'vivid' && (
                      ['#ff6b6b', '#4ecdc4', '#ffbe0b'].map((color, i) => (
                        <div key={i} className="w-4 h-4 rounded-full" style={{ backgroundColor: color, marginLeft: i > 0 ? '-5px' : 0 }}></div>
                      ))
                    )}
                    {palette === 'monochrome' && (
                      ['#0466c8', '#0353a4', '#023e7d'].map((color, i) => (
                        <div key={i} className="w-4 h-4 rounded-full" style={{ backgroundColor: color, marginLeft: i > 0 ? '-5px' : 0 }}></div>
                      ))
                    )}
                  </div>
                  <span className="text-xs capitalize text-gray-700 dark:text-gray-300">{palette}</span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Stok Eşikleri */}
          <div className="space-y-2">
            <h3 className="text-md font-medium text-gray-800 dark:text-white">Stok Eşikleri</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">Kritik Stok Eşiği</label>
                <div className="flex items-center mt-1">
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={settings.criticalStockThreshold}
                    onChange={(e) => handleSettingChange('criticalStockThreshold', parseInt(e.target.value))}
                    className="w-full mr-3 accent-blue-600"
                  />
                  <span className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 px-2 py-1 rounded-md text-xs font-medium w-8 text-center">
                    {settings.criticalStockThreshold}
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">Düşük Stok Eşiği</label>
                <div className="flex items-center mt-1">
                  <input
                    type="range"
                    min={settings.criticalStockThreshold + 1}
                    max="20"
                    value={settings.lowStockThreshold}
                    onChange={(e) => handleSettingChange('lowStockThreshold', parseInt(e.target.value))}
                    className="w-full mr-3 accent-amber-600"
                  />
                  <span className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 px-2 py-1 rounded-md text-xs font-medium w-8 text-center">
                    {settings.lowStockThreshold}
                  </span>
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Stok eşikleri, stok durumu raporlarında ve kritik stok uyarılarında kullanılır.
            </p>
          </div>
          
          {/* Veri Yenileme Aralığı */}
          <div className="space-y-2">
            <h3 className="text-md font-medium text-gray-800 dark:text-white">Veri Yenileme Aralığı</h3>
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => handleSettingChange('dataRefreshInterval', 0)}
                className={`p-2 rounded-lg text-center text-sm ${
                  settings.dataRefreshInterval === 0
                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 font-medium'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                }`}
              >
                Manuel
              </button>
              <button
                onClick={() => handleSettingChange('dataRefreshInterval', 30)}
                className={`p-2 rounded-lg text-center text-sm ${
                  settings.dataRefreshInterval === 30
                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 font-medium'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                }`}
              >
                30 Saniye
              </button>
              <button
                onClick={() => handleSettingChange('dataRefreshInterval', 60)}
                className={`p-2 rounded-lg text-center text-sm ${
                  settings.dataRefreshInterval === 60
                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 font-medium'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                }`}
              >
                1 Dakika
              </button>
            </div>
          </div>
          
          {/* Görünüm Seçenekleri */}
          <div className="space-y-3">
            <h3 className="text-md font-medium text-gray-800 dark:text-white">Görünüm Seçenekleri</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm text-gray-700 dark:text-gray-300">Animasyonları Göster</label>
                <div className="relative inline-block w-10 align-middle select-none">
                  <input
                    type="checkbox"
                    checked={settings.showAnimations}
                    onChange={(e) => handleSettingChange('showAnimations', e.target.checked)}
                    className="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer focus:outline-none transition-transform duration-200 ease-in-out"
                    style={{
                      borderColor: settings.showAnimations ? '#3b82f6' : '#d1d5db',
                      transform: settings.showAnimations ? 'translateX(100%)' : 'translateX(0)',
                    }}
                  />
                  <label className={`block overflow-hidden h-6 rounded-full cursor-pointer transition-colors duration-200 ease-in ${settings.showAnimations ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'}`}></label>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <label className="text-sm text-gray-700 dark:text-gray-300">Trend Göstergeleri</label>
                <div className="relative inline-block w-10 align-middle select-none">
                  <input
                    type="checkbox"
                    checked={settings.displayOptions.showTrends}
                    onChange={(e) => handleDisplayOptionChange('showTrends', e.target.checked)}
                    className="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer focus:outline-none transition-transform duration-200 ease-in-out"
                    style={{
                      borderColor: settings.displayOptions.showTrends ? '#3b82f6' : '#d1d5db',
                      transform: settings.displayOptions.showTrends ? 'translateX(100%)' : 'translateX(0)',
                    }}
                  />
                  <label className={`block overflow-hidden h-6 rounded-full cursor-pointer transition-colors duration-200 ease-in ${settings.displayOptions.showTrends ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'}`}></label>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <label className="text-sm text-gray-700 dark:text-gray-300">Grafik Açıklamalarını Göster</label>
                <div className="relative inline-block w-10 align-middle select-none">
                  <input
                    type="checkbox"
                    checked={settings.displayOptions.showLegends}
                    onChange={(e) => handleDisplayOptionChange('showLegends', e.target.checked)}
                    className="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer focus:outline-none transition-transform duration-200 ease-in-out"
                    style={{
                      borderColor: settings.displayOptions.showLegends ? '#3b82f6' : '#d1d5db',
                      transform: settings.displayOptions.showLegends ? 'translateX(100%)' : 'translateX(0)',
                    }}
                  />
                  <label className={`block overflow-hidden h-6 rounded-full cursor-pointer transition-colors duration-200 ease-in ${settings.displayOptions.showLegends ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'}`}></label>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <label className="text-sm text-gray-700 dark:text-gray-300">Kompakt Görünüm</label>
                <div className="relative inline-block w-10 align-middle select-none">
                  <input
                    type="checkbox"
                    checked={settings.displayOptions.compactView}
                    onChange={(e) => handleDisplayOptionChange('compactView', e.target.checked)}
                    className="absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer focus:outline-none transition-transform duration-200 ease-in-out"
                    style={{
                      borderColor: settings.displayOptions.compactView ? '#3b82f6' : '#d1d5db',
                      transform: settings.displayOptions.compactView ? 'translateX(100%)' : 'translateX(0)',
                    }}
                  />
                  <label className={`block overflow-hidden h-6 rounded-full cursor-pointer transition-colors duration-200 ease-in ${settings.displayOptions.compactView ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'}`}></label>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex items-center justify-end p-5 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setShowSettingsModal(false)}
            className="inline-flex items-center px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg"
          >
            <Check size={18} className="mr-1.5" /> Uygula
          </button>
        </div>
      </motion.div>
    </motion.div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[calc(100vh-200px)]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
        <p className="ml-3 text-lg text-gray-600 dark:text-gray-300 font-medium">İstatistikler Yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col justify-center items-center h-[calc(100vh-200px)] text-red-500 p-4">
        <AlertCircle size={48} className="mb-3" />
        <p className="text-xl font-semibold">Hata Oluştu</p>
        <p className="text-center">{error}</p>
      </div>
    );
  }
  
  if (producibleQuantities.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center h-[calc(100vh-200px)] text-gray-500 dark:text-gray-400 p-4">
        <Package size={48} className="mb-3" />
        <p className="text-xl font-semibold">Veri Yok</p>
        <p className="text-center animate-pulse" style={{ fontSize: '12px' }}>Gösterilecek istatistiksel veri bulunamadı. Lütfen cihaz modellerini ve stok bilgilerini kontrol edin.</p>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm p-3 rounded-lg shadow-lg border border-gray-200/50 dark:border-gray-700/50">
          <p className="font-semibold text-gray-800 dark:text-white">{label}</p>
          <p className="text-sm text-blue-600 dark:text-blue-400">{`Üretilebilir: ${payload[0].value.toLocaleString()} adet`}</p>
        </div>
      );
    }
    return null;
  };
  
  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name, value }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
  
    if (percent * 100 < 5) return null; // Küçük yüzdeleri gösterme

    return (
      <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" className="text-xs font-medium">
        {`${name} (${(percent * 100).toFixed(0)}%)`}
      </text>
    );
  };

  return (
    <div className="min-h-screen transition-all duration-300 ease-in-out">
    <div className="p-6 bg-gradient-to-br from-gray-50 to-blue-50/30 dark:from-gray-900 dark:to-gray-800 min-h-screen transition-all duration-300 ease-in-out">
        {/* Timeframe Change Notification */}
        {timeframeMessage && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-4 right-4 z-50 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg"
          >
            {timeframeMessage}
          </motion.div>
        )}
        
      {/* Üst Dashboard Bar */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-gradient-to-r from-blue-600/90 to-indigo-700/90 backdrop-blur-md dark:from-blue-800/90 dark:to-indigo-900/90 text-white p-8 rounded-2xl shadow-lg mb-8 border border-white/20 dark:border-gray-700/20"
      >
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold mb-2">Montaj İstatistikleri</h2>
            <p className="opacity-90 text-sm">Stok durumu ve üretim kapasitesi analizleri</p>
          </div>
          <div className="flex items-center mt-4 md:mt-0 space-x-3">
            <div className="text-xs opacity-80 flex items-center">
              <Clock size={14} className="mr-1" />
              Son güncelleme: {lastUpdated.toLocaleTimeString()}
            </div>
            <button 
              onClick={handleRefresh} 
              className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
              disabled={loading}
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
            <button 
              onClick={() => setShowSettingsModal(true)}
              className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
              disabled={loading}
            >
              <Settings size={16} />
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            className="bg-white/10 backdrop-blur-sm border border-white/10 p-4 rounded-xl hover:bg-white/20 transition-all duration-300 cursor-pointer group"
          >
            <div className="flex items-center mb-2">
              <Layers size={20} className="mr-2 text-blue-200 group-hover:text-white transition-colors" />
              <span className="text-sm font-medium">Toplam Cihaz</span>
            </div>
            <div className="flex items-end">
              <div className="text-3xl font-bold">{deviceModels.length}</div>
              <div className="ml-2 text-xs opacity-80 mb-1 flex items-center text-emerald-300">
                <TrendingUp size={14} className="mr-0.5" /> +2.4%
              </div>
            </div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="bg-white/10 backdrop-blur-sm border border-white/10 p-4 rounded-xl hover:bg-white/20 transition-all duration-300 cursor-pointer group"
          >
            <div className="flex items-center mb-2">
              <Package size={20} className="mr-2 text-blue-200 group-hover:text-white transition-colors" />
              <span className="text-sm font-medium">Toplam Üretilebilir</span>
            </div>
            <div className="flex items-end">
              <div className="text-3xl font-bold">{totalProducibleUnits.toLocaleString()}</div>
              <div className="ml-2 text-xs opacity-80 mb-1 flex items-center text-emerald-300">
                <TrendingUp size={14} className="mr-0.5" /> +5.1%
              </div>
            </div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="bg-white/10 backdrop-blur-sm border border-white/10 p-4 rounded-xl hover:bg-white/20 transition-all duration-300 cursor-pointer group"
          >
            <div className="flex items-center mb-2">
              <BarChartIcon size={20} className="mr-2 text-blue-200 group-hover:text-white transition-colors" />
              <span className="text-sm font-medium">Kategori Sayısı</span>
            </div>
            <div className="flex items-end">
              <div className="text-3xl font-bold">{categoryProductionData.length}</div>
              <div className="ml-2 text-xs opacity-80 mb-1 flex items-center text-amber-300">
                <TrendingDown size={14} className="mr-0.5" /> +0.0%
              </div>
            </div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4 }}
            className="bg-white/10 backdrop-blur-sm border border-white/10 p-4 rounded-xl hover:bg-white/20 transition-all duration-300 cursor-pointer group"
          >
            <div className="flex items-center mb-2">
              <AlertCircle size={20} className="mr-2 text-blue-200 group-hover:text-white transition-colors" />
              <span className="text-sm font-medium">Kritik Stok</span>
            </div>
            <div className="flex items-end">
              <div className="text-3xl font-bold">{stockStatusData.find(s => s.name === 'Kritik Stok')?.value || 0}</div>
              <div className="ml-2 text-xs opacity-80 mb-1 flex items-center text-red-300">
                <TrendingUp size={14} className="mr-0.5" /> +1.2%
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Zaman Aralığı Filtreleme */}
      <div className="flex justify-between items-center mb-6">
        {/* Sol: Sekme Butonları */}
        <div className="flex flex-wrap gap-2">
          <SectionButton 
            active={activeSection === 'overview'} 
            onClick={() => setActiveSection('overview')}
            icon={<Info size={18} />}
            text="Genel Bakış"
          />
          <SectionButton 
            active={activeSection === 'devices'} 
            onClick={() => setActiveSection('devices')}
            icon={<Package size={18} />}
            text="Cihaz Analizi"
          />
          <SectionButton 
            active={activeSection === 'categories'} 
            onClick={() => setActiveSection('categories')}
            icon={<PieChartIcon size={18} />}
            text="Kategori Analizi"
          />
          <SectionButton 
            active={activeSection === 'stocks'} 
            onClick={() => setActiveSection('stocks')}
            icon={<BarChartIcon size={18} />}
            text="Stok Durumu"
          />
        </div>

        {/* Sağ: Zaman Aralığı Filtreleri */}
        <div className="ml-auto flex gap-1 bg-white/80 dark:bg-gray-800/80 p-1 rounded-lg backdrop-blur-md shadow-sm border border-gray-200/50 dark:border-gray-700/50">
          <button 
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              activeTimeframe === 'weekly' 
                ? 'bg-blue-500 text-white' 
                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
            }`}
            onClick={() => setActiveTimeframe('weekly')}
          >
            Haftalık
          </button>
          <button 
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              activeTimeframe === 'monthly' 
                ? 'bg-blue-500 text-white' 
                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
            }`}
            onClick={() => setActiveTimeframe('monthly')}
          >
            Aylık
          </button>
          <button 
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              activeTimeframe === 'yearly' 
                ? 'bg-blue-500 text-white' 
                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
            }`}
            onClick={() => setActiveTimeframe('yearly')}
          >
            Yıllık
          </button>
        </div>
      </div>

      {/* Ana İçerik - Genel Bakış */}
      {activeSection === 'overview' && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {/* Sol: Ana istatistikler */}
            <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                <StatCard 
                  title="Toplam Üretilebilir Adet" 
                  value={totalProducibleUnits.toLocaleString()} 
                  icon={<Package size={24} />} 
                  color="blue" 
                  trend="+18.2% geçen aya göre"
                  trendUp={true}
                />
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: 0.2 }}
              >
                <StatCard 
                  title="En Çok Üretilebilir" 
                  value={mostProducibleDevice?.name || '-'} 
                  subValue={`${mostProducibleDevice?.producible.toLocaleString() || '0'} adet`} 
                  icon={<TrendingUp size={24} />} 
                  color="green" 
                  trend="+5.4% geçen aya göre"
                  trendUp={true}
                />
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: 0.3 }}
              >
                <StatCard 
                  title="En Az Üretilebilir" 
                  value={leastProducibleDevice?.name || '-'} 
                  subValue={`${leastProducibleDevice?.producible.toLocaleString() || '0'} adet`} 
                  icon={<TrendingDown size={24} />} 
                  color="red" 
                  trend="-2.1% geçen aya göre"
                  trendUp={false}
                />
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: 0.4 }}
              >
                <StatCard 
                  title="Toplam Cihaz Modeli" 
                  value={deviceModels.length.toLocaleString()} 
                  icon={<Layers size={24}/>} 
                  color="purple" 
                  trend="+2.0% geçen aya göre"
                  trendUp={true}
                />
              </motion.div>
            </div>

            {/* Sağ: Üretim Trendi */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-xl shadow-sm border border-gray-100/50 dark:border-gray-700/50 p-4"
            >
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-800 dark:text-white">
                    {activeTimeframe === 'weekly' ? 'Haftalık' : 
                     activeTimeframe === 'yearly' ? 'Yıllık' : 'Aylık'} Stok ve Üretim Karşılaştırması
                  </h3>
                  {activeTimeframe === 'weekly' && (
                    <span className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300 px-2 py-1 rounded-full">
                      Son 7 gün
                    </span>
                  )}
                  {activeTimeframe === 'monthly' && (
                    <span className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300 px-2 py-1 rounded-full">
                      Son 4 hafta
                    </span>
                  )}
                  {activeTimeframe === 'yearly' && (
                    <span className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300 px-2 py-1 rounded-full">
                      Son 12 ay
                    </span>
                  )}
                </div>
              <ResponsiveContainer width="100%" height={240}>
                  <AreaChart data={timeframeData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorUretim" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#4f46e5" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#4f46e5" stopOpacity={0.1}/>
                    </linearGradient>
                    <linearGradient id="colorStok" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: 'currentColor' }} className="text-gray-500 dark:text-gray-400" />
                  <YAxis tick={{ fontSize: 12, fill: 'currentColor' }} className="text-gray-500 dark:text-gray-400" />
                  <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" vertical={false} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.9)', 
                      backdropFilter: 'blur(10px)',
                      borderRadius: '8px',
                      border: '1px solid rgba(229, 231, 235, 0.5)'
                    }}
                      formatter={(value) => [`${value.toLocaleString()} adet`, '']}
                  />
                    <Area type="monotone" dataKey="totalProducible" stroke="#4f46e5" fillOpacity={1} fill="url(#colorUretim)" name="Üretilebilir" />
                    <Area type="monotone" dataKey="totalStock" stroke="#0ea5e9" fillOpacity={1} fill="url(#colorStok)" name="Stok" />
                </AreaChart>
              </ResponsiveContainer>
              <div className="flex items-center justify-center mt-2 gap-6 text-xs text-gray-500 dark:text-gray-400">
                <div className="flex items-center">
                  <div className="w-3 h-3 rounded-full bg-indigo-500 mr-1"></div>
                    <span>Üretilebilir Adet</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 rounded-full bg-blue-500 mr-1"></div>
                    <span>Mevcut Stok</span>
                </div>
              </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-2 italic">
                  {activeTimeframe === 'weekly' 
                    ? 'Kategori bazlı gerçek stok ve üretilebilir ürün miktarları' 
                    : activeTimeframe === 'yearly' 
                      ? 'Cihaz modeline göre gerçek stok ve üretilebilir ürün miktarları' 
                      : 'Kategori bazlı gerçek stok ve üretilebilir ürün miktarları'}
                </p>
                <div className="p-2 mt-2 bg-blue-50/50 dark:bg-blue-900/20 rounded-lg text-center">
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    {activeTimeframe === 'weekly' 
                    ? 'Her kategorideki gerçek durumu göstermektedir'
                    : activeTimeframe === 'yearly'
                    ? 'Her cihaz modelindeki gerçek durumu göstermektedir'
                    : 'Her kategorideki gerçek durumu göstermektedir'}
                  </p>
              </div>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <ChartContainer 
                title="Kritik Stok Ürünleri" 
                icon={<AlertCircle size={20} />}
                className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md"
              >
                  <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
                  {criticalStockProducts.length > 0 ? (
                    criticalStockProducts.map((product, index) => (
                      <div key={product.id} className="flex items-center justify-between p-3 bg-gray-50/70 dark:bg-gray-700/40 backdrop-blur-sm rounded-lg border border-gray-100/50 dark:border-gray-600/50 transition-all hover:bg-gray-100/70 dark:hover:bg-gray-600/30">
                        <div className="flex items-center">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            product.stock <= 2 ? 'bg-red-100/80 text-red-600 dark:bg-red-900/30 dark:text-red-400' : 
                            'bg-amber-100/80 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'
                          }`}>
                            {product.stock}
                          </div>
                          <div className="ml-3">
                            <p className="font-medium text-gray-800 dark:text-gray-200">{product.name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">ID: {product.id}</p>
                          </div>
                        </div>
                        <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                          product.stock <= 2 ? 'bg-red-100/50 text-red-600 dark:bg-red-900/30 dark:text-red-400' : 
                          'bg-amber-100/50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'
                        }`}>
                          {product.stock <= 2 ? 'Kritik Seviye' : 'Düşük Stok'}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center p-6 text-gray-500 dark:text-gray-400">
                      Kritik stokta ürün bulunmuyor
                    </div>
                  )}
                </div>
              </ChartContainer>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              >
                <ChartContainer 
                  title="Stok Sağlık Puanları" 
                  icon={<PieChartIcon size={20} />}
                  className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md"
                >
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Kritik Stok', value: criticalStockProducts.filter(p => p.stock <= 2).length },
                          { name: 'Düşük Stok', value: criticalStockProducts.filter(p => p.stock > 2).length },
                          { name: 'Yeterli Stok', value: allProducts?.length - criticalStockProducts.length || 0 }
                        ]}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={renderCustomizedLabel}
                        outerRadius={120}
                        innerRadius={60}
                        fill="#8884d8"
                        dataKey="value"
                        nameKey="name"
                      >
                        <Cell fill="#ef4444" />
                        <Cell fill="#f59e0b" />
                        <Cell fill="#10b981" />
                      </Pie>
                      <Tooltip 
                        formatter={(value, name) => [`${value} ürün`, name]}
                        contentStyle={{ 
                          backgroundColor: 'rgba(255, 255, 255, 0.9)', 
                          backdropFilter: 'blur(10px)',
                          borderRadius: '8px',
                          border: '1px solid rgba(229, 231, 235, 0.5)'
                        }} 
                      />
                      <Legend wrapperStyle={{fontSize: "12px"}}/>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="mt-4 p-3 bg-blue-50/50 dark:bg-blue-900/20 rounded-lg">
                    <p className="text-xs text-blue-700 dark:text-blue-300 text-center">
                      Kritik stok: 0-2 adet, Düşük stok: 3-5 adet, Yeterli stok: 5+ adet
                    </p>
                  </div>
                </ChartContainer>
              </motion.div>
            </div>
          </>
        )}

        {/* Cihaz Analizi Bölümü */}
        {activeSection === 'devices' && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              {/* Özet Kartları */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                className="lg:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-4"
              >
                <StatCard 
                  title="Toplam Cihaz Modeli" 
                  value={deviceModels.length.toLocaleString()} 
                  icon={<Layers size={24}/>} 
                  color="blue" 
                />
                
                <StatCard 
                  title="En Çok Üretilebilir" 
                  value={mostProducibleDevice?.name || '-'} 
                  subValue={`${mostProducibleDevice?.producible.toLocaleString() || '0'} adet`} 
                  icon={<TrendingUp size={24} />} 
                  color="green" 
                />
                
                <StatCard 
                  title="En Çok Parça Kullanan" 
                  value={deviceModels.reduce((prev, current) => 
                    (prev?.components?.length > current?.components?.length) ? prev : current, {}).name || '-'} 
                  subValue={`${deviceModels.reduce((prev, current) => 
                    (prev?.components?.length > current?.components?.length) ? prev : current, {}).components?.length || 0} farklı parça`} 
                  icon={<Package size={24} />} 
                  color="purple" 
                />
              </motion.div>
            </div>

            {/* Cihaz Üretilebilirlik Grafiği */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-8"
            >
              <ChartContainer 
                title="Cihaz Üretilebilirlik Karşılaştırması" 
                icon={<BarChartIcon size={20} />}
                className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md"
              >
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={producibleQuantities}
                    margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                    <XAxis 
                      dataKey="name" 
                      tick={{ fontSize: 12, fill: 'currentColor' }} 
                      className="text-gray-500 dark:text-gray-400"
                      interval={0}
                      angle={-45}
                      textAnchor="end"
                    />
                    <YAxis 
                      tick={{ fontSize: 12, fill: 'currentColor' }} 
                      className="text-gray-500 dark:text-gray-400" 
                    />
                    <Tooltip
                      contentStyle={{ 
                        backgroundColor: 'rgba(255, 255, 255, 0.9)', 
                        backdropFilter: 'blur(10px)',
                        borderRadius: '8px',
                        border: '1px solid rgba(229, 231, 235, 0.5)'
                      }}
                      formatter={(value, name) => [`${value.toLocaleString()} adet`, 'Üretilebilir']}
                    />
                    <Bar 
                      dataKey="producible" 
                      name="Üretilebilir Miktar" 
                      fill="#4f46e5"
                      radius={[4, 4, 0, 0]}
                    >
                      {producibleQuantities.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={chartColors[index % chartColors.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            </motion.div>

            {/* Cihaz Detayları Kartları */}
            <div className="grid grid-cols-1 gap-6 mb-8">
              <ChartContainer 
                title="Cihaz Modelleri ve Bileşenleri" 
                icon={<Layers size={20} />}
                className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md"
              >
                <div className="space-y-6">
                  {deviceModels.map((device) => (
                    <motion.div
                      key={device.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 bg-gray-50/70 dark:bg-gray-700/40 backdrop-blur-sm rounded-lg border border-gray-100/50 dark:border-gray-600/50"
                    >
                      <div className="flex flex-col md:flex-row justify-between mb-4">
                        <div>
                          <div className="flex items-center">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              device.status === 'Aktif' ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400' :
                              'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'
                            }`}>
                              <Package size={16} />
                            </div>
                            <h3 className="ml-2 text-lg font-medium text-gray-800 dark:text-white">{device.name}</h3>
                          </div>
                          <div className="mt-1 flex flex-wrap gap-2">
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100/50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                              {device.category}
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium 
                              ${device.status === 'Aktif' 
                                ? 'bg-green-100/50 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                : 'bg-amber-100/50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                              }`}
                            >
                              {device.status}
                            </span>
                          </div>
                        </div>
                        
                        <div className="mt-3 md:mt-0 md:text-right">
                          <div className="text-sm text-gray-500 dark:text-gray-400">Üretilebilir Miktar</div>
                          <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
                            {producibleQuantities.find(pq => pq.name === device.name)?.producible || 0} adet
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-4">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Gerekli Bileşenler</h4>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead>
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Parça Adı</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Miktar</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Birim</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Stok Durumu</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                              {device.components && device.components.map((component) => {
                                const stockProduct = allProducts.find(p => p.id === component.productId);
                                const stockStatus = stockProduct 
                                  ? (stockProduct.stock < component.quantity) 
                                    ? 'kritik' // Stok gereken miktardan azsa kritik
                                    : stockProduct.stock <= 2 
                                      ? 'kritik' 
                                      : stockProduct.stock <= 5 
                                        ? 'düşük' 
                                        : 'yeterli'
                                  : 'bilinmiyor';
                                  
                                return (
                                  <tr key={component.productId} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                    <td className="px-3 py-2 text-sm text-gray-800 dark:text-gray-200">{component.productName}</td>
                                    <td className="px-3 py-2 text-sm text-gray-800 dark:text-gray-200">{component.quantity}</td>
                                    <td className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">{component.unit}</td>
                                    <td className="px-3 py-2">
                                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                        stockStatus === 'kritik' 
                                          ? 'bg-red-100/50 text-red-700 dark:bg-red-900/30 dark:text-red-300' 
                                          : stockStatus === 'düşük'
                                          ? 'bg-amber-100/50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                                          : stockStatus === 'yeterli'
                                          ? 'bg-green-100/50 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                          : 'bg-gray-100/50 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300'
                                      }`}>
                                        {stockStatus === 'kritik' 
                                          ? (stockProduct && stockProduct.stock < component.quantity)
                                            ? `Kritik (${stockProduct.stock}/${component.quantity})`
                                            : 'Kritik' 
                                          : stockStatus === 'düşük'
                                          ? 'Düşük'
                                          : stockStatus === 'yeterli'
                                          ? 'Yeterli'
                                          : 'Bilinmiyor'}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </ChartContainer>
            </div>
          </>
        )}

        {/* Kategori Analizi Bölümü */}
        {activeSection === 'categories' && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              {/* Özet Kartları */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                className="lg:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-4"
              >
                <StatCard 
                  title="Toplam Kategori" 
                  value={categoryProductionData.length.toLocaleString()} 
                  icon={<PieChartIcon size={24}/>} 
                  color="blue" 
                />
                
                <StatCard 
                  title="En Verimli Kategori" 
                  value={efficiencyData.reduce((prev, curr) => prev.verimlilik > curr.verimlilik ? prev : curr, {verimlilik: 0}).name || '-'} 
                  subValue={`%${Math.round(efficiencyData.reduce((prev, curr) => prev.verimlilik > curr.verimlilik ? prev : curr, {verimlilik: 0}).verimlilik || 0)} verimlilik`} 
                  icon={<TrendingUp size={24} />} 
                  color="green" 
                />
                
                <StatCard 
                  title="En Yüksek Üretim Kapasitesi" 
                  value={categoryProductionData.reduce((prev, curr) => prev.totalProducible > curr.totalProducible ? prev : curr, {totalProducible: 0}).name || '-'} 
                  subValue={`${categoryProductionData.reduce((prev, curr) => prev.totalProducible > curr.totalProducible ? prev : curr, {totalProducible: 0}).totalProducible.toLocaleString() || 0} adet`} 
                  icon={<Zap size={24} />} 
                  color="purple" 
                />
              </motion.div>
            </div>

            {/* Kategori Dağılımı ve Üretim Kapasitesi */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
              <ChartContainer 
                title="Kategori Dağılımı" 
                icon={<PieChartIcon size={20} />}
                className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md"
              >
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={categoryProductionData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderCustomizedLabel}
                      outerRadius={120}
                      innerRadius={60}
                      fill="#8884d8"
                      dataKey="totalProducible"
                      nameKey="name"
                    >
                      {categoryProductionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={chartColors[index % chartColors.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name) => [`${value.toLocaleString()} adet`, name]}
                      contentStyle={{ 
                        backgroundColor: 'rgba(255, 255, 255, 0.9)', 
                        backdropFilter: 'blur(10px)',
                        borderRadius: '8px',
                        border: '1px solid rgba(229, 231, 235, 0.5)'
                      }} 
                    />
                    <Legend wrapperStyle={{fontSize: "12px"}}/>
                  </PieChart>
                </ResponsiveContainer>
              </ChartContainer>
            </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <ChartContainer 
                  title="Kategori Üretim Kapasitesi" 
                  icon={<BarChartIcon size={20} />}
                  className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md"
                >
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={categoryProductionData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 50 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                      <XAxis 
                        dataKey="name" 
                        tick={{ fontSize: 12, fill: 'currentColor' }} 
                        className="text-gray-500 dark:text-gray-400"
                        interval={0}
                        angle={-45}
                        textAnchor="end"
                      />
                      <YAxis 
                        tick={{ fontSize: 12, fill: 'currentColor' }} 
                        className="text-gray-500 dark:text-gray-400" 
                      />
                      <Tooltip
                        contentStyle={{ 
                          backgroundColor: 'rgba(255, 255, 255, 0.9)', 
                          backdropFilter: 'blur(10px)',
                          borderRadius: '8px',
                          border: '1px solid rgba(229, 231, 235, 0.5)'
                        }}
                        formatter={(value, name) => [`${value.toLocaleString()} adet`, name === 'totalProducible' ? 'Üretilebilir Miktar' : 'Cihaz Sayısı']}
                      />
                      <Bar 
                        dataKey="totalProducible" 
                        name="Üretilebilir Miktar" 
                        fill="#4f46e5"
                        radius={[4, 4, 0, 0]}
                      >
                        {categoryProductionData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={chartColors[index % chartColors.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            </motion.div>
          </div>

            {/* Üretim Verimliliği ve Kategori Detayları */}
            <div className="grid grid-cols-1 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
          >
            <ChartContainer 
              title="Kategori Bazlı Üretim Verimliliği" 
              icon={<Zap size={20} />}
              className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md"
            >
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {efficiencyData.map((category) => (
                  <div 
                    key={category.name} 
                    className="bg-gray-50/70 dark:bg-gray-700/40 backdrop-blur-sm p-4 rounded-xl border border-gray-100/50 dark:border-gray-600/50"
                  >
                    <div className="flex justify-between mb-2">
                      <h4 className="font-medium text-gray-800 dark:text-white">{category.name}</h4>
                      <span className={`font-bold ${
                        category.verimlilik >= 90 ? 'text-emerald-500' : 
                        category.verimlilik >= 80 ? 'text-blue-500' : 
                        category.verimlilik >= 70 ? 'text-amber-500' : 'text-red-500'
                      }`}>
                        {category.verimlilik}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          category.verimlilik >= 90 ? 'bg-emerald-500' : 
                          category.verimlilik >= 80 ? 'bg-blue-500' : 
                          category.verimlilik >= 70 ? 'bg-amber-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${category.verimlilik}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
                  <div className="mt-6">
                    <ChartContainer 
                      title="Kategori Detayları" 
                      icon={<Info size={20} />}
                      className="bg-gray-50/70 dark:bg-gray-800/50 backdrop-blur-sm"
                    >
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                          <thead>
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Kategori</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Cihaz Sayısı</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Üretilebilir</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Verimlilik</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Durum</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {categoryProductionData.map((category, index) => {
                              const efficiency = efficiencyData.find(e => e.name === category.name)?.verimlilik || 0;
                              const status = 
                                efficiency >= 90 ? 'Mükemmel' : 
                                efficiency >= 80 ? 'İyi' : 
                                efficiency >= 70 ? 'Orta' : 'Geliştirilebilir';
                              
                              return (
                                <tr key={category.name} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                  <td className="px-3 py-2 text-sm text-gray-800 dark:text-gray-200">
                                    <div className="flex items-center">
                                      <div className="w-3 h-3 rounded-full mr-2" style={{backgroundColor: chartColors[index % chartColors.length]}}></div>
                                      {category.name}
                                    </div>
                                  </td>
                                  <td className="px-3 py-2 text-sm text-gray-800 dark:text-gray-200">{category.deviceCount}</td>
                                  <td className="px-3 py-2 text-sm text-gray-800 dark:text-gray-200">{category.totalProducible.toLocaleString()} adet</td>
                                  <td className="px-3 py-2 text-sm">
                                    <span className={`font-medium ${
                                      efficiency >= 90 ? 'text-emerald-500' : 
                                      efficiency >= 80 ? 'text-blue-500' : 
                                      efficiency >= 70 ? 'text-amber-500' : 'text-red-500'
                                    }`}>
                                      %{efficiency}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2">
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                      efficiency >= 90 ? 'bg-emerald-100/50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 
                                      efficiency >= 80 ? 'bg-blue-100/50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 
                                      efficiency >= 70 ? 'bg-amber-100/50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' : 
                                      'bg-red-100/50 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                                    }`}>
                                      {status}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </ChartContainer>
              </div>
            </ChartContainer>
          </motion.div>
            </div>
        </>
      )}

        {/* Stok Durumu Bölümü */}
        {activeSection === 'stocks' && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              {/* Özet Kartları */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                className="lg:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-4"
              >
                <StatCard 
                  title="Toplam Stok Miktarı" 
                  value={allProducts?.reduce((sum, item) => sum + item.stock, 0).toLocaleString() || '0'} 
                  icon={<PackageIcon size={24}/>} 
                  color="blue" 
                />
                
                <StatCard 
                  title="Kritik Stok Ürünleri" 
                  value={criticalStockProducts.length.toLocaleString()} 
                  subValue="Acil sipariş gerekli" 
                  icon={<AlertCircle size={24} />} 
                  color="red" 
                />
                
                <StatCard 
                  title="Stok Sağlık Durumu" 
                  value={`%${Math.round(((allProducts?.length - criticalStockProducts.length) / (allProducts?.length || 1)) * 100)}`} 
                  subValue={`${allProducts?.length - criticalStockProducts.length} / ${allProducts?.length} ürün yeterli stokta`} 
                  icon={<ActivityIcon size={24} />} 
                  color={criticalStockProducts.length > allProducts?.length * 0.2 ? "red" : "green"} 
                />
              </motion.div>
            </div>

            {/* Stok Sağlık Puanları ve Kritik Stok Ürünleri */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <ChartContainer 
                  title="Stok Durumu Dağılımı" 
                  icon={<PieChartIcon size={20} />}
                  className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md"
                >
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Kritik Stok', value: criticalStockProducts.filter(p => p.stock <= 2).length },
                          { name: 'Düşük Stok', value: criticalStockProducts.filter(p => p.stock > 2).length },
                          { name: 'Yeterli Stok', value: allProducts?.length - criticalStockProducts.length || 0 }
                        ]}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={renderCustomizedLabel}
                        outerRadius={120}
                        innerRadius={60}
                        fill="#8884d8"
                        dataKey="value"
                        nameKey="name"
                      >
                        <Cell fill="#ef4444" />
                        <Cell fill="#f59e0b" />
                        <Cell fill="#10b981" />
                      </Pie>
                      <Tooltip 
                        formatter={(value, name) => [`${value} ürün`, name]}
                        contentStyle={{ 
                          backgroundColor: 'rgba(255, 255, 255, 0.9)', 
                          backdropFilter: 'blur(10px)',
                          borderRadius: '8px',
                          border: '1px solid rgba(229, 231, 235, 0.5)'
                        }} 
                      />
                      <Legend wrapperStyle={{fontSize: "12px"}}/>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="mt-4 p-3 bg-blue-50/50 dark:bg-blue-900/20 rounded-lg">
                    <p className="text-xs text-blue-700 dark:text-blue-300 text-center">
                      Kritik stok: 0-2 adet, Düşük stok: 3-5 adet, Yeterli stok: 5+ adet
                    </p>
                  </div>
                </ChartContainer>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <ChartContainer 
                  title="Kritik Stok Ürünleri" 
                  icon={<AlertCircle size={20} />}
                  className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md"
                >
                  <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
                    {criticalStockProducts.length > 0 ? (
                      criticalStockProducts.map((product, index) => (
                        <div key={product.id} className="flex items-center justify-between p-3 bg-gray-50/70 dark:bg-gray-700/40 backdrop-blur-sm rounded-lg border border-gray-100/50 dark:border-gray-600/50 transition-all hover:bg-gray-100/70 dark:hover:bg-gray-600/30">
                          <div className="flex items-center">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              product.stock <= 2 ? 'bg-red-100/80 text-red-600 dark:bg-red-900/30 dark:text-red-400' : 
                              'bg-amber-100/80 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'
                            }`}>
                              {product.stock}
                            </div>
                            <div className="ml-3">
                              <p className="font-medium text-gray-800 dark:text-gray-200">{product.name}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">ID: {product.id}</p>
                            </div>
                          </div>
                          <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                            product.stock <= 2 ? 'bg-red-100/50 text-red-600 dark:bg-red-900/30 dark:text-red-400' : 
                            'bg-amber-100/50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'
                          }`}>
                            {product.stock <= 2 ? 'Kritik Seviye' : 'Düşük Stok'}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center p-6 text-gray-500 dark:text-gray-400">
                        Kritik stokta ürün bulunmuyor
                      </div>
                    )}
                  </div>
                </ChartContainer>
              </motion.div>
            </div>

            {/* Kategori Bazlı Stok Durumu */}
            <div className="grid grid-cols-1 gap-6 mb-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <ChartContainer 
                  title="Stok Durumu Tablosu" 
                  icon={<ListIcon size={20} />}
                  className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md"
                >
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
                    <div className="flex flex-wrap gap-2">
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded-full bg-red-500"></div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">Kritik (0-2)</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">Düşük (3-5)</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">Yeterli (5+)</span>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                      <div className="relative w-full sm:w-auto">
                        <input 
                          type="text" 
                          placeholder="Ürün ara..." 
                          className="pl-8 pr-10 py-1.5 text-sm bg-gray-50/70 dark:bg-gray-700/40 rounded-md border border-gray-200/50 dark:border-gray-600/50 focus:outline-none focus:ring-2 focus:ring-blue-500/50 w-full"
                          value={searchText}
                          onChange={handleSearchChange}
                        />
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        {searchText && (
                          <button 
                            onClick={() => setSearchText('')}
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 focus:outline-none"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                      
                      <select 
                        className={`px-2 py-1.5 text-sm border border-gray-200/50 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${
                          statusFilter === 'all' 
                            ? 'bg-gray-50/70 dark:bg-gray-700/40' 
                            : statusFilter === 'kritik'
                            ? 'bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                            : statusFilter === 'düşük'
                            ? 'bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                            : 'bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                        }`}
                        value={statusFilter}
                        onChange={handleStatusFilterChange}
                      >
                        <option value="all">Tüm Durumlar</option>
                        <option value="kritik">Kritik</option>
                        <option value="düşük">Düşük</option>
                        <option value="yeterli">Yeterli</option>
                      </select>
                      
                      <select 
                        className={`px-2 py-1.5 text-sm bg-gray-50/70 dark:bg-gray-700/40 rounded-md border border-gray-200/50 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${
                          sortOption !== 'name' ? 'text-blue-600 dark:text-blue-400 font-medium' : ''
                        }`}
                        value={sortOption}
                        onChange={handleSortChange}
                      >
                        <option value="name">Sırala: İsim</option>
                        <option value="name_desc">Sırala: İsim (Ters)</option>
                        <option value="stock_asc">Sırala: Stok (Az-Çok)</option>
                        <option value="stock_desc">Sırala: Stok (Çok-Az)</option>
                      </select>
                    </div>
                    
                    {/* Aktif filtreler göstergesi */}
                    {(searchText || statusFilter !== 'all') && (
                      <div className="w-full flex flex-wrap items-center gap-2 mt-2 text-xs text-gray-600 dark:text-gray-400">
                        <span>Aktif filtreler:</span>
                        {searchText && (
                          <span className="px-2 py-1 bg-blue-100/50 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 rounded-full flex items-center">
                            Arama: {searchText}
                            <button 
                              onClick={() => setSearchText('')}
                              className="ml-1 text-blue-500 hover:text-blue-700 dark:hover:text-blue-200 focus:outline-none"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </span>
                        )}
                        {statusFilter !== 'all' && (
                          <span className={`px-2 py-1 rounded-full flex items-center ${
                            statusFilter === 'kritik' 
                              ? 'bg-red-100/50 text-red-800 dark:bg-red-900/30 dark:text-red-300' 
                              : statusFilter === 'düşük'
                              ? 'bg-amber-100/50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                              : 'bg-green-100/50 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          }`}>
                            Durum: {statusFilter}
                            <button 
                              onClick={() => setStatusFilter('all')}
                              className="ml-1 focus:outline-none"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </span>
                        )}
                        {(searchText || statusFilter !== 'all') && (
                          <button 
                            onClick={() => {
                              setSearchText('');
                              setStatusFilter('all');
                            }}
                            className="px-2 py-1 bg-gray-100/50 text-gray-700 dark:bg-gray-800/50 dark:text-gray-300 rounded-full text-xs hover:bg-gray-200 dark:hover:bg-gray-700 focus:outline-none"
                          >
                            Tümünü Temizle
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="mt-6 flex flex-col sm:flex-row justify-between items-center">
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-3 sm:mb-0">
                      {filteredProducts.length === 0 ? (
                        <span className="text-amber-600 dark:text-amber-400">
                          Eşleşen ürün bulunamadı. Lütfen filtreleri temizleyin veya arama kriterlerini değiştirin.
                        </span>
                      ) : allProducts?.length === filteredProducts.length ? (
                        <>Toplam <span className="font-medium">{filteredProducts.length}</span> ürün görüntüleniyor</>
                      ) : (
                        <>Toplam <span className="font-medium">{filteredProducts.length}</span> ürün filtrelendi (toplam {allProducts?.length} üründen)</>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button disabled className="px-3 py-1.5 text-sm border border-gray-200/50 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-800/70 text-gray-400 dark:text-gray-500 rounded-md">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <button className="px-3 py-1.5 text-sm bg-blue-500 text-white dark:bg-blue-600 rounded-md hover:bg-blue-600">1</button>
                      <button className="px-3 py-1.5 text-sm border border-gray-200/50 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-800/70 text-gray-400 dark:text-gray-500 rounded-md" disabled>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Stok Durumu Tablosu */}
                  <div className="overflow-x-auto rounded-lg border border-gray-200/50 dark:border-gray-700/50 mt-4">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50/70 dark:bg-gray-700/40">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            <div className="flex items-center cursor-pointer hover:text-gray-700 dark:hover:text-gray-200" onClick={toggleSortByName}>
                              Ürün Adı
                              <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ml-1 transition-transform ${sortOption === 'name_desc' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                              </svg>
                            </div>
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">ID</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            <div className="flex items-center cursor-pointer hover:text-gray-700 dark:hover:text-gray-200" onClick={toggleSortByStock}>
                              Stok
                              <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ml-1 transition-transform ${sortOption === 'stock_desc' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                              </svg>
                            </div>
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Stok Durumu</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">İşlemler</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white/80 dark:bg-gray-800/80 divide-y divide-gray-200 dark:divide-gray-700">
                        {filteredProducts.length > 0 ? (
                          filteredProducts.map((product) => {
                            const stockStatus = 
                              product.stock <= 2 ? 'kritik' : 
                              product.stock <= 5 ? 'düşük' : 'yeterli';
                              
                            return (
                              <tr key={product.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                <td className="px-4 py-3 text-sm text-gray-800 dark:text-gray-200 font-medium">
                                  <div className="flex items-center">
                                    {stockStatus === 'kritik' && (
                                      <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse mr-2"></span>
                                    )}
                                    {product.name}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{product.id}</td>
                                <td className="px-4 py-3 text-sm font-medium">
                                  <div className="flex items-center">
                                    <span className={`
                                      ${stockStatus === 'kritik' ? 'text-red-600 dark:text-red-400' : 
                                        stockStatus === 'düşük' ? 'text-amber-600 dark:text-amber-400' : 
                                        'text-green-600 dark:text-green-400'}
                                    `}>
                                      {product.stock.toLocaleString()}
                                    </span>
                                  </div>
                                  
                                  {/* Stok ilerleme çubuğu */}
                                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 mt-1">
                                    <div 
                                      className={`h-1.5 rounded-full ${
                                        stockStatus === 'kritik' ? 'bg-red-500' : 
                                        stockStatus === 'düşük' ? 'bg-amber-500' : 
                                        'bg-green-500'
                                      }`}
                                      style={{ width: `${Math.min(product.stock * 10, 100)}%` }}
                                    ></div>
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    stockStatus === 'kritik' 
                                      ? 'bg-red-100/50 text-red-700 dark:bg-red-900/30 dark:text-red-300' : 
                                      stockStatus === 'düşük'
                                      ? 'bg-amber-100/50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                                      : 'bg-green-100/50 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                  }`}>
                                    {stockStatus === 'kritik' 
                                      ? 'Kritik' 
                                      : stockStatus === 'düşük'
                                      ? 'Düşük'
                                      : 'Yeterli'}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm">
                                  <div className="flex space-x-2">
                                    <button className="p-1 rounded-md bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100">
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                      </svg>
                                    </button>
                                    <button className="p-1 rounded-md bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 hover:bg-green-100">
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                                      </svg>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={5} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400 italic">
                              Eşleşen ürün bulunamadı. Lütfen filtreleri temizleyin.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </ChartContainer>
              </motion.div>
            </div>
          </>
        )}
      </div>
    </div>

  );
};

const StatCard = ({ title, value, subValue, icon, color, trend, trendUp }) => {
  const colors = {
    blue: 'from-blue-500/10 to-blue-600/5',
    green: 'from-emerald-500/10 to-emerald-600/5',
    red: 'from-rose-500/10 to-rose-600/5',
    purple: 'from-violet-500/10 to-violet-600/5',
  };
  
  const borderColors = {
    blue: 'border-blue-200/30 dark:border-blue-800/30',
    green: 'border-emerald-200/30 dark:border-emerald-800/30',
    red: 'border-rose-200/30 dark:border-rose-800/30',
    purple: 'border-violet-200/30 dark:border-violet-800/30',
  };
  
  const textColors = {
    blue: 'text-blue-600 dark:text-blue-400',
    green: 'text-emerald-600 dark:text-emerald-400',
    red: 'text-rose-600 dark:text-rose-400',
    purple: 'text-violet-600 dark:text-violet-400',
  };
  
  return (
    <div className={`p-6 rounded-xl shadow-sm bg-gradient-to-br ${colors[color] || 'from-gray-500/10 to-gray-600/5'} backdrop-blur-md h-full border ${borderColors[color] || 'border-gray-200/30 dark:border-gray-700/30'} hover:shadow-md transition-shadow group`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-300 group-hover:text-gray-800 dark:group-hover:text-white transition-colors">{title}</h3>
        <div className={`p-2 rounded-lg ${textColors[color]}`}>
          {icon}
        </div>
      </div>
      <p className="text-2xl font-bold text-gray-900 dark:text-white mb-1">{value}</p>
      {subValue && <p className="text-sm opacity-80 text-gray-600 dark:text-gray-400">{subValue}</p>}
      
      {trend && (
        <div className={`mt-3 text-xs flex items-center ${trendUp ? 'text-emerald-500' : 'text-rose-500'}`}>
          {trendUp ? <TrendingUp size={14} className="mr-1" /> : <TrendingDown size={14} className="mr-1" />}
          <span>{trend}</span>
        </div>
      )}
    </div>
  );
};

const ChartContainer = ({ title, children, icon, className = "" }) => (
  <div className={`p-6 rounded-xl shadow-sm border border-gray-100/50 dark:border-gray-700/50 h-full ${className}`}>
    <h2 className="text-lg font-medium text-gray-800 dark:text-white mb-6 pb-3 border-b border-gray-200/50 dark:border-gray-700/50 flex items-center">
      <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 mr-3">
        {icon || <BarChartIcon size={20} />}
      </span>
      {title}
    </h2>
    {children}
  </div>
);

const SectionButton = ({ active, onClick, icon, text }) => (
  <button
    onClick={onClick}
    className={`py-2.5 px-4 rounded-lg flex items-center transition-all duration-200 ${
      active 
        ? 'bg-blue-600/90 text-white backdrop-blur-sm shadow-sm' 
        : 'bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm text-gray-700 dark:text-gray-300 border border-gray-200/50 dark:border-gray-700/50 hover:bg-gray-50/80 dark:hover:bg-gray-700/80'
    }`}
  >
    {icon}
    <span className="ml-2 font-medium">{text}</span>
    {active && <ArrowRight size={16} className="ml-2" />}
  </button>
);

const CustomizedContent = (props) => {
  const { root, depth, x, y, width, height, index, payload, rank, name } = props;
  const color = COLORS[index % COLORS.length];

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        style={{
          fill: color,
          stroke: '#fff',
          strokeWidth: 2 / (depth + 1e-10),
          strokeOpacity: 1 / (depth + 1e-10),
        }}
      />
      {depth === 1 && width * height > 2000 ? (
        <text
          x={x + width / 2}
          y={y + height / 2 + 7}
          textAnchor="middle"
          fill="#fff"
          fontSize={14}
          fontWeight="bold"
        >
          {name} {payload && payload.value !== undefined ? `(${payload.value})` : ''}
        </text>
      ) : null}
       {depth === 1 && width * height <= 2000 && width * height > 500 ? (
        <text x={x + 4} y={y + 18} fill="#fff" fontSize={10} fillOpacity={0.9}>
          {name && name.length > 10 ? `${name.substring(0,10)}...` : name}
        </text>
      ) : null}
    </g>
  );
};

export default MakineMontajIstatistikleri; 